
import { useState } from 'react';
import { PasswordStrengthInput } from "./PasswordStrengthInput";
import { PasswordStrengthResult } from '@/utils/passwordStrength';

import { UserRole } from '@/types/user-roles';

interface SignUpFormProps {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  organizationName: string;
  isSubmitting: boolean;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onOrganizationNameChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  passwordStrength: PasswordStrengthResult | null;
  onPasswordStrengthChange: (result: PasswordStrengthResult | null) => void;
}

export const SignUpForm = ({
  firstName,
  lastName,
  email,
  password,
  organizationName,
  isSubmitting,
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
  onPasswordChange,
  onOrganizationNameChange,
  onSubmit,
  passwordStrength,
  onPasswordStrengthChange,
}: SignUpFormProps) => {
  
  const [showPasswordWarning, setShowPasswordWarning] = useState(false);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    let hasErrors = false;

    // Check password strength
    if (!passwordStrength || !passwordStrength.isValid) {
      setShowPasswordWarning(true);
      hasErrors = true;
    } else {
      setShowPasswordWarning(false);
    }
    
    if (hasErrors) {
      return;
    }

    onSubmit(e);
  };

  return (
    <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
      <div className="rounded-md shadow-sm space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="firstName" className="sr-only">
              First name
            </label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              required
              className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
              placeholder="Jane"
              value={firstName}
              onChange={(e) => onFirstNameChange(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="lastName" className="sr-only">
              Last name
            </label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              required
              className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
              placeholder="Doe"
              value={lastName}
              onChange={(e) => onLastNameChange(e.target.value)}
            />
          </div>
        </div>
        <div>
          <label htmlFor="organizationName" className="sr-only">
            Organization name
          </label>
          <input
            id="organizationName"
            name="organizationName"
            type="text"
            required
            className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
            placeholder="Your organization name"
            value={organizationName}
            onChange={(e) => onOrganizationNameChange(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="email" className="sr-only">
            Email address
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
            placeholder="jane@organization.org"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
          />
        </div>
        
        {/* Password Strength Input */}
        <div>
          <label htmlFor="password" className="sr-only">
            Password
          </label>
          <PasswordStrengthInput
            value={password}
            onChange={onPasswordChange}
            placeholder="Create a secure password"
            onStrengthChange={onPasswordStrengthChange}
            showWeakPasswordError={showPasswordWarning}
          />
        </div>
      </div>
    </form>
  );
};
